/* AgenticMetricsPanel — adoption Agentic V1 (Ultra-Admin, agrégats stricts).
 * Métriques falsifiables : jamais optimisées artificiellement. */
import { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const FAM_LABELS = {
  UNDERSTAND: "Comprendre", ANALYZE: "Analyser", PREPARE: "Préparer", MONITOR: "Surveiller",
  DECIDE: "Décider", ACT: "Agir", SEARCH: "Rechercher",
};

function Kpi({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
      <div className="text-lg font-semibold" style={{ color: accent || "#fff" }}>{value ?? "—"}</div>
      <div className="text-[10px] text-zinc-400">{label}</div>
    </div>
  );
}

export default function AgenticMetricsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/workspace/agentic/metrics/global")
      .then((r) => setData(r.data)).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  if (!data) return <div className="text-xs text-zinc-500 py-6">Métriques agentic indisponibles.</div>;
  const ev = data.events || {};
  const rate = data.intent_to_result_completion_rate;
  return (
    <div className="space-y-4" data-testid="agentic-metrics-panel">
      <div className="flex items-center gap-2 text-sm font-semibold text-white">
        <Sparkles className="w-4 h-4 text-[#378ADD]" /> Agentic V1 : adoption réelle (toutes organisations)
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Kpi label="Intentions exprimées" value={ev.intent_routed || 0} accent="#378ADD" />
        <Kpi label="Premières valeurs" value={ev.first_value || 0} accent="#1D9E75" />
        <Kpi label="Taux intention → résultat" value={rate == null ? "—" : `${Math.round(rate * 100)}%`} accent="#EF9F27" />
        <Kpi label="Échecs / abandons" value={data.failures || 0} accent={data.failures ? "#EF4444" : undefined} />
        <Kpi label="Agent → Instrument" value={ev.agent_to_instrument || 0} />
        <Kpi label="Instrument → Agent" value={ev.instrument_to_agent || 0} />
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Familles d'intentions</div>
          {Object.entries(data.intent_families || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-[11px] py-0.5">
              <span className="text-zinc-300">{FAM_LABELS[k] || k}</span>
              <span className="text-[#378ADD] font-semibold">{v}</span>
            </div>
          ))}
          {!Object.keys(data.intent_families || {}).length && <div className="text-[11px] text-zinc-600">Aucune encore</div>}
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Routage</div>
          {Object.entries(data.routing || {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-[11px] py-0.5">
              <span className="text-zinc-300">{k === "deterministic" ? "Déterministe" : "Fallback LLM"}</span>
              <span className="text-white font-semibold">{v}</span>
            </div>
          ))}
          <div className="flex items-center justify-between text-[11px] py-0.5 mt-2 border-t border-white/5 pt-1.5">
            <span className="text-zinc-400">Environnements actifs</span>
            <span className="text-white">{data.environments_active}</span>
          </div>
          <div className="flex items-center justify-between text-[11px] py-0.5">
            <span className="text-zinc-400">Utilisateurs actifs</span>
            <span className="text-white">{data.users_active}</span>
          </div>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Modes d'expérience</div>
          <div className="flex items-center justify-between text-[11px] py-0.5">
            <span className="text-zinc-300">Agentic</span>
            <span className="text-[#378ADD] font-semibold">{data.mode_distribution?.agentic ?? 0}</span>
          </div>
          <div className="flex items-center justify-between text-[11px] py-0.5">
            <span className="text-zinc-300">Expert</span>
            <span className="text-white font-semibold">{data.mode_distribution?.expert ?? 0}</span>
          </div>
          <div className="text-[10px] text-zinc-600 mt-2">Sans champ = défaut selon date de création du compte.</div>
        </div>
      </div>
    </div>
  );
}
